/**
 * TerraMatrix Data Service - Server API Service
 *
 * Admin API for managing MongoDB servers and databases.
 * Connects to the data-service backend (port 8002).
 */

import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useReAuthStore, isReAuthModalOpen, triggerReAuth } from '@/stores/auth/reauth-store';
import { config as appConfig } from '@/config';
import {
  applyDataServiceOrganizationHeaders,
  getValidatedDataServiceOrganizationId,
} from './data-service-client';
import { getValidatedExplicitOrganization } from './client';
import {
  getCurrentAuthTokens,
  getCurrentUserEmail,
} from './auth-session';

// Data service base URL (runs on port 8002)
const DATA_SERVICE_URL = appConfig.api.dataServiceUrl;
const INVALID_EXPLICIT_ORGANIZATION_MESSAGE =
  'Organization context unavailable for this request';

const getScopedOrganizationId = async (organizationId?: string): Promise<string | null> => {
  if (organizationId) {
    return (
      await getValidatedExplicitOrganization({ organizationId })
    )?._id ?? null;
  }

  return getValidatedDataServiceOrganizationId();
};

const getScopedRequestConfig = (organizationId?: string | null) =>
  organizationId
    ? {
        headers: {
          'X-Organization-Id': organizationId,
        },
      }
    : undefined;

const getValidatedExplicitScopedRequestConfig = async (
  organizationId?: string | null,
) => {
  if (!organizationId) {
    return undefined;
  }

  const scopedOrganizationId = await getScopedOrganizationId(organizationId);
  if (!scopedOrganizationId) {
    throw new Error(INVALID_EXPLICIT_ORGANIZATION_MESSAGE);
  }

  return getScopedRequestConfig(scopedOrganizationId);
};

// Create axios instance for data service
export const createDataServiceClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: DATA_SERVICE_URL,
    timeout: 30000, // 30 seconds
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor for authentication
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const tokens = getCurrentAuthTokens();

      if (tokens?.accessToken) {
        config.headers.Authorization = `Bearer ${tokens.accessToken}`;
      }

      return applyDataServiceOrganizationHeaders(config, {
        allowUnscopedHealthCheck: false,
      });
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor — handle 401 with re-auth modal (same pattern as workflowApi)
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        const hadToken = getCurrentAuthTokens();
        const userEmail = getCurrentUserEmail();

        if (hadToken?.accessToken) {
          originalRequest._retry = true;

          if (!isReAuthModalOpen()) {
            triggerReAuth('token_invalid', userEmail || undefined);
          }

          return new Promise((resolve, reject) => {
            useReAuthStore.getState().addPendingRequest({
              config: originalRequest,
              resolve: (result) => {
                if (result === 'retry') {
                  const newTokens = getCurrentAuthTokens();
                  if (newTokens?.accessToken) {
                    originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
                  }
                  resolve(client(originalRequest));
                } else {
                  reject(new Error('Re-authentication required'));
                }
              },
              reject: (err) => reject(err),
            });
          });
        }
      }

      const apiError = {
        code: error.response?.data?.error || 'UNKNOWN_ERROR',
        message: error.response?.data?.message || error.message || 'An unexpected error occurred',
        details: error.response?.data?.details,
      };
      return Promise.reject(apiError);
    }
  );

  return client;
};

const dataServiceClient = createDataServiceClient();

// ============================================================================
// Types
// ============================================================================

export type MongoServerStatus = 'active' | 'inactive';
export type MongoServerHealthStatus = 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
export type DatabaseConnectionStatus = 'active' | 'inactive';

export interface MongoServerHealth {
  lastCheck?: string;
  status: MongoServerHealthStatus;
  latencyMs?: number;
  version?: string;
  errorMessage?: string;
}

export interface MongoServer {
  id: string;
  name: string;
  host: string;
  port: number;
  authDatabase?: string;
  username?: string;
  status: MongoServerStatus;
  health: MongoServerHealth;
  organizationId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DatabaseConnection {
  id: string;
  name: string;
  description?: string;
  connectionString: string;
  username?: string;
  isDefault: boolean;
  status: DatabaseConnectionStatus;
  organizationId?: string | null;
  serverId?: string | null;
  lastHealthCheck?: string;
  lastHealthStatus?: 'healthy' | 'unhealthy' | 'unknown';
  lastHealthError?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateServerInput {
  name: string;
  host: string;
  port?: number;
  authDatabase?: string;
  username?: string;
  password?: string;
  replicaSet?: string;
  organizationId?: string | null;
}

export interface UpdateServerInput {
  name?: string;
  host?: string;
  port?: number;
  authDatabase?: string;
  username?: string;
  password?: string;
  replicaSet?: string;
  status?: MongoServerStatus;
}

export interface CreateDatabaseInput {
  databaseName: string;
  description?: string;
  isDefault?: boolean;
}

export interface ServerTestResult {
  success: boolean;
  latencyMs: number;
  serverInfo?: {
    version: string;
    host: string;
  };
  databases?: string[];
  error?: string;
}

export interface ServerWithDatabases {
  server: MongoServer;
  databases: DatabaseConnection[];
}

export interface ListDatabasesResult {
  registered: DatabaseConnection[];
  unregistered: string[];
}

// ============================================================================
// API Functions
// ============================================================================

export const serverApi = {
  // =========================================================================
  // Server Management API
  // =========================================================================

  /**
   * List all MongoDB servers
   */
  listServers: async (organizationId?: string): Promise<MongoServer[]> => {
    const scopedOrganizationId = await getScopedOrganizationId(organizationId);
    if (!scopedOrganizationId) {
      return [];
    }

    const params = { organizationId: scopedOrganizationId };
    const response = await dataServiceClient.get('/api/admin/servers', {
      params,
      headers: { 'X-Organization-Id': scopedOrganizationId },
    });
    return response.data.data || response.data;
  },

  /**
   * Get a server with its databases
   */
  getServer: async (id: string): Promise<ServerWithDatabases> => {
    const response = await dataServiceClient.get(`/api/admin/servers/${id}`, getScopedRequestConfig());
    return response.data.data || response.data;
  },

  /**
   * Create a new server registration
   */
  createServer: async (input: CreateServerInput): Promise<MongoServer> => {
    const requestConfig = await getValidatedExplicitScopedRequestConfig(
      input.organizationId,
    );
    const response = await dataServiceClient.post(
      '/api/admin/servers',
      input,
      requestConfig,
    );
    return response.data.data || response.data;
  },

  /**
   * Update a server
   */
  updateServer: async (id: string, input: UpdateServerInput): Promise<MongoServer> => {
    const response = await dataServiceClient.put(
      `/api/admin/servers/${id}`,
      input,
      getScopedRequestConfig(),
    );
    return response.data.data || response.data;
  },

  /**
   * Delete a server (soft delete)
   */
  deleteServer: async (id: string): Promise<void> => {
    await dataServiceClient.delete(`/api/admin/servers/${id}`, getScopedRequestConfig());
  },

  /**
   * Test a server connection
   */
  testServer: async (id: string): Promise<ServerTestResult> => {
    const response = await dataServiceClient.post(
      `/api/admin/servers/${id}/test`,
      undefined,
      getScopedRequestConfig(),
    );
    return response.data.data || response.data;
  },

  /**
   * Test a server connection by config (before saving)
   */
  testServerConnection: async (input: CreateServerInput): Promise<ServerTestResult> => {
    const requestConfig = await getValidatedExplicitScopedRequestConfig(
      input.organizationId,
    );
    const response = await dataServiceClient.post(
      '/api/admin/servers/test-connection',
      input,
      requestConfig,
    );
    return response.data.data || response.data;
  },

  /**
   * Create a database on a server
   */
  createDatabase: async (serverId: string, input: CreateDatabaseInput): Promise<DatabaseConnection> => {
    const response = await dataServiceClient.post(
      `/api/admin/servers/${serverId}/databases`,
      input,
      getScopedRequestConfig(),
    );
    return response.data.data || response.data;
  },

  /**
   * List databases on a server (registered and unregistered)
   */
  listDatabases: async (serverId: string): Promise<ListDatabasesResult> => {
    const response = await dataServiceClient.get(
      `/api/admin/servers/${serverId}/databases`,
      getScopedRequestConfig(),
    );
    return response.data.data || response.data;
  },

  // =========================================================================
  // Database Connection API (existing endpoints)
  // =========================================================================

  /**
   * List all database connections
   */
  listConnections: async (organizationId?: string): Promise<DatabaseConnection[]> => {
    const scopedOrganizationId = await getScopedOrganizationId(organizationId);
    if (!scopedOrganizationId) {
      return [];
    }

    const params = { organizationId: scopedOrganizationId };
    const response = await dataServiceClient.get('/api/admin/databases', {
      params,
      headers: { 'X-Organization-Id': scopedOrganizationId },
    });
    return response.data.data || response.data;
  },

  /**
   * Get a database connection
   */
  getConnection: async (id: string): Promise<DatabaseConnection> => {
    const response = await dataServiceClient.get(`/api/admin/databases/${id}`, getScopedRequestConfig());
    return response.data.data || response.data;
  },

  /**
   * Test a database connection
   */
  testConnection: async (id: string): Promise<{ success: boolean; latencyMs: number; error?: string }> => {
    const response = await dataServiceClient.post(
      `/api/admin/databases/${id}/test`,
      undefined,
      getScopedRequestConfig(),
    );
    return response.data.data || response.data;
  },

  /**
   * Set a connection as default
   */
  setDefaultConnection: async (id: string): Promise<DatabaseConnection> => {
    const response = await dataServiceClient.post(
      `/api/admin/databases/${id}/set-default`,
      undefined,
      getScopedRequestConfig(),
    );
    return response.data.data || response.data;
  },

  /**
   * Delete a database connection (soft delete)
   */
  deleteConnection: async (id: string): Promise<void> => {
    await dataServiceClient.delete(`/api/admin/databases/${id}`, getScopedRequestConfig());
  },
};

export default serverApi;
